import { alert } from "@components/common/Alert";
import { MarkdownContent } from "@components/design-system/atoms/MarkdownContent";
import { DSCopyButton } from "@components/design-system/organisms";
import {
  faArrowUp,
  faDownload,
  faFileLines,
  faPaperclip,
  faSpinner,
  faStop,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  AvailableCompanions,
  AvailableCompanionTabs,
  AvailableSettingsSections,
  useCompanion,
} from "@hooks/use-companion";
import {
  useCompanionV2,
  CompanionV2Message,
  CompanionV2Attachment,
  CompanionEffort,
  CompanionIdentity,
  PendingAttachment,
  AGENT_ATTACHMENT_ACCEPT,
  MAX_ATTACHMENTS_PER_MESSAGE,
} from "@hooks/use-companion-v2";
import { useEscalateToSupport } from "@hooks/use-escalate-to-support";
import Twemoji from "@src/Twemoji";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { CompanionEffortBadge } from "./CompanionEffortBadge";
import { CompanionTaskStatus } from "./CompanionTaskStatus";
import { NavProposalCard } from "./NavProposalCard";
import { OttoThinking } from "./OttoThinking";
import { pickRandomPrompts } from "./suggestedPrompts";
import { THINKING_STATES_BY_COMPANION } from "./thinkingStates";
import { ToolPermissionCard } from "./ToolPermissionCard";

const WELCOME_TEAM = ["OTTO", "SUMMI", "SAGE", "QUILL"] as const;

export const CompanionV2Chat = () => {
  const {
    messages,
    isStreaming,
    isCreatingSession,
    isResumedSession,
    error,
    isHydrating,
    hydrationError,
    hasMoreHistory,
    isLoadingMoreHistory,
    hasPendingToolPermission,
    effort,
    pendingAttachments,
    isStagingAttachment,
    isBackgrounded,
    isStopping,
    sendMessage,
    stopGeneration,
    retry,
    clearSession,
    loadMoreHistory,
    retryHydration,
    stageAttachment,
    removePendingAttachment,
    escalationRequest,
    clearEscalationRequest,
  } = useCompanionV2();
  const { setActiveTab, setSettingsSection } = useCompanion();
  useEscalateToSupport({
    request: escalationRequest,
    onFired: clearEscalationRequest,
  });

  const goToBilling = () => {
    setSettingsSection(AvailableSettingsSections.billing);
    setActiveTab(AvailableCompanionTabs.settings);
  };

  // Forward-only per-message attribution snapshot — later personas must
  // not retroactively change icons on earlier base-agent bubbles.
  const messageFallbacks = useMemo<
    ReadonlyMap<string, readonly CompanionIdentity[]>
  >(() => {
    const out = new Map<string, readonly CompanionIdentity[]>();
    const running: CompanionIdentity[] = [];
    for (const m of messages) {
      if (m.role === "assistant" && m.companions) {
        for (const c of m.companions) {
          if (!running.includes(c)) running.push(c);
        }
      }
      out.set(m.id, [...running]);
    }
    return out;
  }, [messages]);

  const [input, setInput] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const messageContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Anchor scroll position when prepending older messages so the user's
  // current view stays put instead of jumping to the new top.
  const preservedScrollRef = useRef<{
    prevScrollHeight: number;
    prevScrollTop: number;
  } | null>(null);

  const handleLoadMore = () => {
    const container = messageContainerRef.current;
    if (container) {
      preservedScrollRef.current = {
        prevScrollHeight: container.scrollHeight,
        prevScrollTop: container.scrollTop,
      };
    }
    loadMoreHistory();
  };

  useLayoutEffect(() => {
    const container = messageContainerRef.current;
    const anchor = preservedScrollRef.current;
    if (container && anchor) {
      container.scrollTop =
        anchor.prevScrollTop +
        (container.scrollHeight - anchor.prevScrollHeight);
    }
  }, [messages]);

  // Auto-scroll when new messages arrive or streaming ends (but not
  // when we just prepended older messages via "Load earlier"). The
  // anchor ref is cleared *here* so this effect can see it — if we
  // cleared it in useLayoutEffect above, this effect would run
  // afterwards with a null ref and scroll to bottom anyway, undoing
  // the anchor restoration.
  useEffect(() => {
    if (preservedScrollRef.current) {
      preservedScrollRef.current = null;
      return;
    }
    const container = messageContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages.length, isStreaming, isHydrating]);

  // Auto-focus input
  useEffect(() => {
    if (!isStreaming) {
      inputRef.current?.focus();
    }
  }, [isStreaming]);

  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    const effectiveHeight = el.value ? el.scrollHeight : 36;
    const capped = Math.min(effectiveHeight, 160);
    el.style.height = `${capped}px`;
    el.style.overflowY = effectiveHeight > 160 ? "auto" : "hidden";
  }, [input]);

  const handleSend = () => {
    const text = input.trim();
    const hasAttachments = pendingAttachments.length > 0;
    if (
      (!text && !hasAttachments) ||
      isStreaming ||
      isCreatingSession ||
      isHydrating ||
      isStagingAttachment ||
      isBackgrounded ||
      hasPendingToolPermission
    )
      return;
    setInput("");
    sendMessage(text);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleAttachClick = () => {
    if (
      isStreaming ||
      isStagingAttachment ||
      isHydrating ||
      isBackgrounded
    )
      return;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = Array.from(e.target.files ?? []);
    // Clear the input so the same file can be re-selected after remove.
    if (fileInputRef.current) fileInputRef.current.value = "";
    await handleFiles(files);
  };

  const handleFiles = async (files: File[]) => {
    // Parallel upload: ensureSession coalesces the POST /session, and
    // separate S3 presign + PUT for each file happen concurrently.
    const results = await Promise.all(files.map(stageAttachment));
    let capAlerted = false;
    for (let i = 0; i < files.length; i++) {
      const result = results[i];
      const file = files[i];
      if (!result) continue;
      if (result.kind === "too-large") {
        alert(
          "error",
          `"${file.name}" is too large (max ${result.maxMB} MB).`,
        );
      } else if (result.kind === "unsupported-type") {
        alert(
          "error",
          `"${file.name}" isn't a supported file type. Allowed: PDF, Office docs, images, CSV, TXT.`,
        );
      } else if (result.kind === "cap-reached") {
        // One alert for the whole batch regardless of how many files
        // spilled past the cap.
        if (!capAlerted) {
          alert(
            "error",
            `You can attach at most ${result.max} files per message.`,
          );
          capAlerted = true;
        }
      } else {
        alert("error", `Couldn't upload "${file.name}". Try again.`);
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragEnter = () => {
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Only set isDragging to false if we're leaving the entire component
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (
      isStreaming ||
      isStagingAttachment ||
      isHydrating ||
      isBackgrounded
    ) {
      return;
    }
    const files = Array.from(e.dataTransfer.files ?? []);
    if (files.length > 0) {
      await handleFiles(files);
    }
  };

  const sendDisabled =
    (!input.trim() && pendingAttachments.length === 0) ||
    isStreaming ||
    isCreatingSession ||
    isHydrating ||
    isStagingAttachment ||
    isBackgrounded ||
    hasPendingToolPermission;
  const attachDisabled =
    isStreaming ||
    isStagingAttachment ||
    isHydrating ||
    isBackgrounded ||
    pendingAttachments.length >= MAX_ATTACHMENTS_PER_MESSAGE;

  // Loading state while session creates
  if (isCreatingSession && messages.length === 0) {
    return (
      <div className="flex flex-1 flex-col h-full">
        <div className="rounded-xl bg-primary flex flex-1 flex-col items-center justify-center p-2 text-primary overflow-hidden">
          <FontAwesomeIcon
            icon={faSpinner}
            size="2x"
            className="animate-spin text-brand"
          />
        </div>
      </div>
    );
  }

  const showWelcome =
    messages.length === 0 &&
    !isStreaming &&
    !isResumedSession &&
    !isHydrating;

  return (
    <div className="flex flex-1 flex-col h-full">
      <div
        className={`rounded-xl bg-primary flex flex-1 flex-col justify-between p-2 text-primary overflow-hidden transition-all ${
          isDragging ? "ring-2 ring-brand ring-inset bg-brand/5" : ""
        }`}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {!isResumedSession && (
          <div className="flex items-center justify-end gap-xs px-1 pb-1">
            <CompanionEffortBadge effort={effort} />
          </div>
        )}

        {/* Messages */}
        <div
          className="flex flex-col flex-1 gap-2 overflow-auto py-2 overscroll-contain"
          ref={messageContainerRef}
        >
          {hasMoreHistory && !isHydrating && (
            <div className="flex justify-center py-xs">
              <button
                onClick={handleLoadMore}
                disabled={isLoadingMoreHistory}
                aria-busy={isLoadingMoreHistory}
                aria-label="Load earlier messages"
                data-testid="companion-v2-load-earlier"
                className="text-xs text-brand hover:underline disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                {isLoadingMoreHistory && (
                  <FontAwesomeIcon
                    icon={faSpinner}
                    className="animate-spin text-[10px]"
                  />
                )}
                {isLoadingMoreHistory
                  ? "Loading…"
                  : "Load earlier messages"}
              </button>
            </div>
          )}

          {isHydrating && messages.length === 0 && (
            <div
              className="flex flex-1 items-center justify-center"
              data-testid="companion-v2-hydrating"
            >
              <FontAwesomeIcon
                icon={faSpinner}
                size="lg"
                className="animate-spin text-brand"
              />
            </div>
          )}

          {showWelcome && (
            <WelcomeScreen
              effort={effort}
              onPromptClick={(prompt) => {
                if (!isStreaming && !isBackgrounded)
                  sendMessage(prompt);
              }}
            />
          )}

          {messages.map((msg) =>
            msg.kind === "nav_proposal" ? (
              <NavProposalCard key={msg.id} message={msg} />
            ) : msg.kind === "tool_permission" ? (
              <ToolPermissionCard key={msg.id} message={msg} />
            ) : msg.kind === "interrupt" ? (
              <div
                key={msg.id}
                className="flex justify-end -mt-2  pr-sm text-xs text-danger"
                data-testid="companion-v2-interrupt-marker"
              >
                Stopped by you
              </div>
            ) : (
              <V2TextBubble
                key={msg.id}
                message={msg}
                isStreaming={isStreaming}
                fallbackCompanions={
                  messageFallbacks.get(msg.id) ?? []
                }
              />
            ),
          )}

          {hydrationError && (
            <div
              className="mx-sm p-sm bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-300 rounded-lg text-sm flex items-center justify-between gap-2"
              data-testid="companion-v2-hydration-error"
            >
              <span>Couldn't load prior messages.</span>
              <button
                onClick={retryHydration}
                className="shrink-0 px-3 py-1 bg-red-100 dark:bg-red-800/30 hover:bg-red-200 dark:hover:bg-red-800/50 rounded-md text-red-700 dark:text-red-200 text-xs transition-colors"
              >
                Retry
              </button>
            </div>
          )}

          {error && !isBackgrounded && (
            <div className="mx-sm p-sm bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-300 rounded-lg text-sm flex items-center justify-between gap-2">
              <span>
                {error.content ||
                  "Something went wrong. Please try again."}
              </span>
              {error.isOutOfTreats ? (
                <button
                  onClick={goToBilling}
                  className="shrink-0 px-3 py-1 bg-red-100 dark:bg-red-800/30 hover:bg-red-200 dark:hover:bg-red-800/50 rounded-md text-red-700 dark:text-red-200 text-xs transition-colors"
                >
                  Purchase more Treats
                </button>
              ) : (
                error.canRetry && (
                  <button
                    onClick={retry}
                    className="shrink-0 px-3 py-1 bg-red-100 dark:bg-red-800/30 hover:bg-red-200 dark:hover:bg-red-800/50 rounded-md text-red-700 dark:text-red-200 text-xs transition-colors"
                  >
                    Retry
                  </button>
                )
              )}
            </div>
          )}

          {isBackgrounded && (
            <div
              role="status"
              aria-live="polite"
              className="mx-sm p-sm bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 rounded-lg text-sm flex items-center justify-between gap-2"
              data-testid="companion-v2-backgrounded"
            >
              <span>
                This conversation is still running in the background.
                Start a new one to keep chatting.
              </span>
              <button
                onClick={clearSession}
                className="shrink-0 px-3 py-1 bg-amber-100 dark:bg-amber-800/30 hover:bg-amber-200 dark:hover:bg-amber-800/50 rounded-md text-amber-800 dark:text-amber-200 text-xs transition-colors"
              >
                Start new conversation
              </button>
            </div>
          )}
        </div>

        {/* Pending attachments staged for the next send */}
        {(pendingAttachments.length > 0 || isStagingAttachment) && (
          <div
            className="flex flex-wrap gap-1 px-sm pt-1"
            data-testid="companion-v2-pending-attachments"
          >
            {pendingAttachments.map((a) => (
              <PendingAttachmentChip
                key={a.uuid}
                attachment={a}
                onRemove={() => removePendingAttachment(a.uuid)}
              />
            ))}
            {isStagingAttachment && (
              <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-1 text-xs text-secondary">
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="animate-spin"
                />
                Uploading…
              </span>
            )}
          </div>
        )}

        {/* Input — matches v1 CompanionChatInput style */}
        <div className="flex flex-row items-center gap-2 pt-2">
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            multiple
            accept={AGENT_ATTACHMENT_ACCEPT}
            onChange={handleFileChange}
            data-testid="companion-v2-file-input"
          />
          <button
            onClick={handleAttachClick}
            disabled={attachDisabled}
            title="Attach file"
            aria-label="Attach file"
            className="rounded-xl p-2 text-secondary transition-colors hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-30"
            data-testid="companion-v2-attach-btn"
          >
            <FontAwesomeIcon icon={faPaperclip} />
          </button>
          <textarea
            ref={inputRef}
            data-testid="companion-v2-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              isBackgrounded
                ? "Start a new conversation to continue..."
                : isStreaming
                  ? "Thinking..."
                  : isHydrating
                    ? "Loading history..."
                    : hasPendingToolPermission
                      ? "Respond to the request above to continue..."
                      : "Ask a Companion to help..."
            }
            disabled={
              isStreaming ||
              isCreatingSession ||
              isHydrating ||
              isBackgrounded ||
              hasPendingToolPermission
            }
            rows={1}
            className="flex-1 bg-secondary text-primary placeholder-secondary rounded-xl px-md py-2 resize-none outline-none text-sm max-h-40 overflow-hidden border border-transparent focus:border-brand transition-colors"
          />
          {isStreaming ? (
            <button
              onClick={stopGeneration}
              disabled={isStopping}
              title="Stop generating"
              aria-label="Stop generating"
              data-testid="companion-v2-stop"
              className="p-2 rounded-xl bg-danger text-white hover:bg-danger/80 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <FontAwesomeIcon
                icon={isStopping ? faSpinner : faStop}
                className={isStopping ? "animate-spin" : ""}
              />
            </button>
          ) : (
            <button
              onClick={handleSend}
              disabled={sendDisabled}
              title="Send message"
              aria-label="Send message"
              data-testid="companion-v2-send"
              className="p-2 rounded-xl bg-brand text-white hover:bg-brand/80 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <FontAwesomeIcon icon={faArrowUp} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/** Welcome screen with suggested prompts — uses existing design tokens */
const WelcomeScreen = ({
  effort,
  onPromptClick,
}: {
  effort: CompanionEffort;
  onPromptClick: (prompt: string) => void;
}) => {
  const [displayPrompts] = useState(() => pickRandomPrompts(4));

  if (effort === "support") {
    const otto = AvailableCompanions.OTTO;
    return (
      <div className="flex flex-col items-center justify-center h-full px-md py-lg">
        {otto?.headImage && (
          <img
            src={otto.headImage}
            alt={otto.displayName}
            className="w-14 h-14 rounded-full object-cover mb-md"
          />
        )}
        <p className="text-secondary text-sm text-center whitespace-normal">
          Hi, I&apos;m Otto. Ask me anything about using Scribenote.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center h-full px-md py-lg">
      <div className="flex gap-sm mb-md">
        {WELCOME_TEAM.map((type) => {
          const companion = AvailableCompanions[type];
          if (!companion?.headImage) return null;
          return (
            <img
              key={type}
              src={companion.headImage}
              alt={companion.displayName}
              className="w-14 h-14 rounded-full object-cover"
            />
          );
        })}
      </div>
      <p className="text-secondary text-sm mb-md text-center whitespace-normal">
        We&apos;re your Companions and we&apos;re ready to help! Here
        are some things you can ask us:
      </p>

      <div className="w-full space-y-2">
        {displayPrompts.map((prompt) => (
          <button
            key={prompt}
            onClick={() => onPromptClick(prompt)}
            className="w-full text-left px-md py-sm bg-secondary hover:bg-tertiary text-primary rounded-lg text-sm transition-colors"
          >
            {prompt}
          </button>
        ))}
      </div>
    </div>
  );
};

type CompanionEntry =
  (typeof AvailableCompanions)[keyof typeof AvailableCompanions];

/** Single primary avatar pinned to the bubble's bottom-left. We pick
 *  the last companion in the resolved list (most recent persona to
 *  contribute to the turn) — the same "most recent" rule the task-
 *  status toast uses for its current avatar. While `isThinking` and
 *  the primary persona is Otto, swap the static head for the animated
 *  OttoThinking icon. */
const BubbleCompanionAvatar = ({
  companions,
  isThinking = false,
}: {
  companions: CompanionEntry[];
  isThinking?: boolean;
}) => {
  if (companions.length === 0) return null;
  const primary = companions[companions.length - 1];
  const showOttoThinking =
    isThinking && primary.name === AvailableCompanions.OTTO.name;
  return (
    <div
      className="absolute -left-12 -bottom-1 text-lg"
      aria-label={`Companion in this message: ${primary.displayName}`}
    >
      {showOttoThinking ? (
        <div className="w-9 h-9">
          <OttoThinking />
        </div>
      ) : primary.headImage ? (
        <img
          src={primary.headImage}
          alt={primary.displayName}
          className="w-9 h-9 rounded-full object-cover"
        />
      ) : primary.emoji ? (
        <Twemoji emoji={primary.emoji} />
      ) : null}
    </div>
  );
};

/** Message bubble — matches v1 TextBubble style */
const V2TextBubble = ({
  message,
  isStreaming,
  fallbackCompanions,
}: {
  message: CompanionV2Message;
  isStreaming: boolean;
  fallbackCompanions: readonly CompanionIdentity[];
}) => {
  const isUser = message.role === "user";
  const isLast = isStreaming && !isUser;
  // Base-agent messages (no companionsUsed) fall back to the running
  // union of companions seen in this conversation — if none yet, Otto.
  const sourceList = !isUser
    ? message.companions?.length
      ? message.companions
      : fallbackCompanions
    : [];
  const resolved = sourceList
    .map((c) => getCompanion(c))
    .filter((c): c is NonNullable<typeof c> => c !== null);
  const companions = !isUser
    ? resolved.length > 0
      ? resolved
      : [AvailableCompanions.OTTO]
    : [];
  const hasCompanions = companions.length > 0;
  const hasAttachments = (message.attachments?.length ?? 0) > 0;
  const hasToolCalls =
    !isUser && (message.toolCalls?.length ?? 0) > 0;

  if (!message.text && !isLast && !hasAttachments && !hasToolCalls)
    return null;

  return (
    <div
      className={`w-full flex flex-col px-sm my-xs ${
        isUser ? "items-end" : "items-start"
      }`}
      data-testid={`companion-message-${message.id}`}
    >
      {/* Tool-call status toast — shows above the response bubble for
          this turn, summarises the chain of tools the agent ran. */}
      {hasToolCalls && (
        <div className="mb-2 w-full">
          <CompanionTaskStatus chain={message.toolCalls!} />
        </div>
      )}
      {/* Hide an empty placeholder bubble while a tool chain is
          rendering — the toast already shows progress. Once the
          assistant streams text in, the bubble re-appears below. */}
      {(message.text ||
        hasAttachments ||
        (isLast && !hasToolCalls)) && (
        <div
          className={`relative max-w-[80%] ${
            isUser
              ? "bg-brand text-white"
              : message.isError
                ? "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-300"
                : `bg-secondary text-primary ${hasCompanions ? "ml-12" : ""}`
          } rounded-large px-md py-sm`}
        >
          {/* Companion avatars — fully stacked behind the first by
              default, fan upward on hover. Each card = one contributor.
              While Otto is the primary and we're waiting on first text,
              swap the static head for the animated thinking icon. */}
          {hasCompanions && (
            <BubbleCompanionAvatar
              companions={companions}
              isThinking={isLast && !message.text}
            />
          )}
          {/* Chat bubble tail */}
          <div
            className={`absolute ${
              isUser
                ? "-right-1 bottom-0 bg-indigo-400"
                : message.isError
                  ? "-left-1 bottom-0 bg-red-50 dark:bg-red-900/20"
                  : "-left-1 bottom-0 bg-secondary"
            } w-2 h-2 rounded-full`}
          />

          {/* Attachments (both user uploads and agent-produced files) */}
          {hasAttachments && (
            <div className="mb-1 flex flex-col gap-1">
              {message.attachments!.map((a) => (
                <AttachmentChip
                  key={a.uuid}
                  attachment={a}
                  onDark={isUser}
                />
              ))}
            </div>
          )}

          {/* Message content */}
          <div
            className={`whitespace-pre-wrap wrap-break-word text-sm ${
              isUser ? "selection-invert" : ""
            }`}
          >
            {isUser ? (
              message.text
            ) : message.text ? (
              <MarkdownContent
                content={message.text}
                className="text-sm"
                isCompanion
              />
            ) : (
              isLast &&
              (() => {
                // Match BubbleCompanionAvatar's primary pick — the
                // most recent companion in the resolved list — so the
                // thinking caption stays in sync with the avatar
                // instead of cycling a different companion's voice.
                const primary = companions[companions.length - 1];
                return (
                  <ThinkingDots
                    states={
                      primary
                        ? THINKING_STATES_BY_COMPANION[primary.name]
                        : undefined
                    }
                  />
                );
              })()
            )}
          </div>

          {/* Copy button */}
          {!isUser && message.text && !message.isError && (
            <div className="absolute -right-10 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white text-indigo-600 shadow-sm flex items-center justify-center dark:bg-gray-700/50 dark:text-gray-300 dark:hover:bg-gray-700">
              <DSCopyButton
                text={message.text}
                dataTestId="companion-v2-copy"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/** Pill for an attachment already registered on the session. */
const AttachmentChip = ({
  attachment,
  onDark,
}: {
  attachment: CompanionV2Attachment;
  onDark: boolean;
}) => {
  const classes = `inline-flex max-w-full items-center gap-1 rounded-full px-2 py-1 text-xs ${
    onDark
      ? "bg-white/15 text-white"
      : "bg-brand/10 text-brand hover:bg-brand/20"
  }`;
  const icon = attachment.fileLink ? faDownload : faFileLines;
  const content = (
    <>
      <FontAwesomeIcon icon={icon} />
      <span className="truncate">{attachment.fileName}</span>
    </>
  );
  if (attachment.fileLink) {
    return (
      <a
        href={attachment.fileLink}
        target="_blank"
        rel="noopener noreferrer"
        className={classes}
        data-testid="companion-v2-attachment-link"
      >
        {content}
      </a>
    );
  }
  return <span className={classes}>{content}</span>;
};

/** Pending chip for a staged, not-yet-sent attachment under the composer. */
const PendingAttachmentChip = ({
  attachment,
  onRemove,
}: {
  attachment: PendingAttachment;
  onRemove: () => void;
}) => (
  <span
    className="inline-flex items-center gap-1 rounded-full bg-brand/10 px-2 py-1 text-xs text-brand"
    data-testid="companion-v2-pending-attachment"
  >
    <FontAwesomeIcon icon={faFileLines} />
    <span className="max-w-[160px] truncate">
      {attachment.fileName}
    </span>
    <button
      type="button"
      onClick={onRemove}
      className="hover:text-brand/70"
      aria-label={`Remove ${attachment.fileName}`}
    >
      <FontAwesomeIcon icon={faXmark} />
    </button>
  </span>
);

/** Animated dots — shows the agent is thinking/working.
 *  If `states` is provided, also cycles through a playful caption. */
const ThinkingDots = ({ states }: { states?: string[] }) => {
  const count = states?.length ?? 0;
  const [stateIdx, setStateIdx] = useState(() =>
    count ? Math.floor(Math.random() * count) : 0,
  );

  useEffect(() => {
    if (!count) return;
    const id = setInterval(() => {
      setStateIdx((prev) => (prev + 1) % count);
    }, 3500);
    return () => clearInterval(id);
  }, [count]);

  return (
    <div className="flex items-center gap-sm py-1">
      <div className="flex gap-1">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="w-1.5 h-1.5 bg-current rounded-full animate-bounce opacity-50"
            style={{ animationDelay: `${i * 150}ms` }}
          />
        ))}
      </div>
      {states && states[stateIdx] && (
        <div className="text-xs italic opacity-70">
          {states[stateIdx]}…
        </div>
      )}
    </div>
  );
};

/** Look up companion metadata from the v1 AvailableCompanions registry */
function getCompanion(companion: CompanionIdentity | undefined) {
  if (!companion) return null;
  const key = companion.toUpperCase();
  return (
    AvailableCompanions[key as keyof typeof AvailableCompanions] ??
    null
  );
}
